import type { WorkflowDefinition } from "../types";

/**
 * W15 — Connect website and content work to AI visibility.
 *
 * `shared`: this is the property's own content setup, and everyone on the team
 * is working the same one. `observed`, because a site whose pages are already
 * imported and grouped really is part-way through it.
 *
 * The one dated step is the note on the timeline: a note explains a change,
 * and a note written last quarter does not explain this quarter's work.
 */
export const contentWorkWorkflow: WorkflowDefinition = {
	id: "content-work",
	wNumber: 15,
	title: "Connect your website work to AI visibility",
	outcome:
		"Tie the pages you publish to what AI answers say, so content work has a number.",
	audiences: ["inhouse", "content"],
	uses: [
		"Property Pages",
		"Projects",
		"Annotations",
		"AI Visibility Rankings",
	],
	scope: "property",
	concurrency: "shared",
	progress: "observed",
	isApplicable: (mode) => mode.showSitemapImport && mode.showProjects,
	articleSlug: "how-to-connect-content-work-to-ai-visibility",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "import-pages",
			title: "Import the pages on your site",
			page: "Property Pages",
			route: "/pages",
			criterion: "You've imported at least one page.",
			why: "Nothing can be measured page by page until the pages are here. The sitemap import brings them all in at once.",
			docsHref: "/docs/dashboards/property-pages",
			tasks: [
				"Import your sitemap",
				"Check the intent labels on your most important pages",
			],
			complete: { kind: "count", key: "pageCount", gte: 1 },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Pages imported",
							value: state.property.pageCount,
							target: 1,
						}
					: null,
		},
		{
			id: "group-pages",
			title: "Group the pages by the work you plan",
			page: "Property Pages",
			route: "/pages",
			criterion: "You've created at least one page group.",
			why: "A page group is how a content push becomes one thing you can measure. Blog, docs and pricing pages move at different speeds.",
			docsHref: "/docs/dashboards/property-pages",
			tasks: [
				"Create a group for each section you are working on",
				"Keep groups small enough to attribute a change to",
			],
			complete: { kind: "count", key: "pageGroupCount", gte: 1 },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Page groups",
							value: state.property.pageGroupCount,
							target: 1,
						}
					: null,
		},
		{
			id: "page-group-goal",
			title: "Set up a project with a page-group goal",
			page: "Projects",
			route: "/projects/new",
			criterion: "You've set a page-group goal on a project.",
			why: "The goal is what links a group of pages to the answers that cite them. Without it, content work and AI visibility stay separate numbers.",
			docsHref: "/docs/dashboards/projects",
			tasks: [
				"Create an SEO project and choose your prompts",
				"Add the page group as a goal",
				"Check the Data Accuracy card before you save",
			],
			complete: { kind: "count", key: "goalTypes.PAGE_GROUP", gte: 1 },
			spend: { kind: "trackedPrompts", count: "fromState" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "checklist",
							items: [
								{
									label: "SEO project",
									done: state.property.projectTypes.SEO > 0,
								},
								{
									label: "Page-group goal",
									done:
										state.property.goalTypes.PAGE_GROUP > 0,
								},
							],
						}
					: null,
		},
		{
			id: "note-releases",
			title: "Note every release on the timeline",
			page: "Annotations",
			route: "/annotations",
			criterion: "You've noted a release since you started.",
			why: "A publish date on the chart is what turns a wobble into a result. Six weeks later nobody remembers when the pages went live.",
			docsHref: "/docs/dashboards/annotations",
			tasks: [
				"Note the day each batch of pages goes live",
				"Note migrations and template changes too",
			],
			// Dated on purpose, even though the rest of this playbook reads
			// standing setup: notes written before you opened it explain
			// somebody else's release, not the work you are doing now.
			complete: { kind: "since", event: "annotationCreated" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Notes on the timeline",
							value: state.property.manualAnnotationCount,
						}
					: null,
		},
		{
			id: "watch-rankings",
			title: "Watch the pages move in AI answers",
			page: "AI Visibility Rankings",
			route: "/ai-visibility-rankings",
			criterion: "You've got daily data flowing.",
			why: "Rankings show whether AI reaches for your pages on the searches it makes. That is the payoff for the content work.",
			docsHref: "/docs/dashboards/ai-visibility-rankings",
			tasks: [
				"Check the rank on the searches your pages target",
				"Read every movement against your release notes",
			],
			complete: { kind: "flag", key: "hasNightlyMetrics" },
			enrichment: "rankTrend",
			// Rank history is not in the loader; the step panel hydrates it
			// from the SEO-opportunities hook rather than inventing a series.
			signal: () => null,
		},
	],
};
